import { useEffect, useRef, useState } from "react";
import { cn } from "utils/helpers";
import { scopeKey, scopeTypeStyle, useScopeOptions } from "newui/features/RolesPermissions";
import type {
	ScopeExclusion,
	ScopeOption,
	ScopeType,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export interface ScopePickerListProps {
	type: ScopeType;
	typeLabel: string;
	query: string;
	exclude: ScopeExclusion[];
	/** Present only in single-target mode (CONTRACT §3.5). */
	userId?: number;
	roleId?: number;
	isSelected: (option: ScopeOption) => boolean;
	onToggle: (option: ScopeOption) => void;
	onAlreadyGrantedChange: (type: ScopeType, options: ScopeOption[]) => void;
	enabled?: boolean;
	/**
	 * Author item 4.5 / ticket lines 49-50 (G6, `project_type` only) — when the company has
	 * exactly one project type, the ticket asks for the SAME "Project Types" header (unchanged)
	 * but the existing single row relabelled `All Projects` rather than showing its real name.
	 * Applies to every row this list renders (in practice there is only ever one, since that is
	 * the precondition under which the caller passes this at all).
	 */
	rowLabelOverride?: string;
}

/**
 * How a row's cascade parent is introduced. Only `project` and `project_type`
 * can ever BE a parent (the chain is rfx ⊂ project ⊂ project_type), so those are
 * the two that get a word; anything else falls back to the bare label rather
 * than inventing one.
 */
const parentPrefix = (parentType: ScopeType): string => {
	if (parentType === "project") {
		return "Project: ";
	}

	if (parentType === "project_type") {
		return "Portfolio: ";
	}

	return "";
};

// One independently-paginated, exclusion-aware list (D8-T2, rules 1/3/4). The
// picker mounts three of these (project_type / project / rfx), all reading the
// SAME cross-type `exclude[]` from `useScopeSelection`.
export const ScopePickerList = ({
	type,
	typeLabel,
	query,
	exclude,
	userId,
	roleId,
	isSelected,
	onToggle,
	onAlreadyGrantedChange,
	enabled = true,
	rowLabelOverride,
}: ScopePickerListProps) => {
	// §3.4 — any change to the exclusion set OR the search text must reset this
	// list's pagination back to page 1: a previous page may have already omitted
	// rows that a deselect would otherwise resurrect (server-side exclusion can't
	// be "undone" client-side by paging further). Adjusting state during render
	// (React's documented pattern for resetting derived state on a prop change)
	// avoids rendering one frame of stale accumulated items against the new key.
	const resetSignature = JSON.stringify({ query, exclude, userId, roleId });
	const [committedSignature, setCommittedSignature] = useState(resetSignature);
	const [page, setPage] = useState(1);
	const [items, setItems] = useState<ScopeOption[]>([]);
	const loadedPagesRef = useRef<Set<number>>(new Set());

	/**
	 * Author item 4.2 root cause (2026-09-24) — "already granted" rows were previously reported
	 * by re-deriving them from `items` on every change (see the effect this replaces, below).
	 * That created a genuine infinite render loop, independent of and pre-dating any Joyride
	 * interaction (confirmed by instrumented render-counting: 7700+ renders/second with no user
	 * action once a real already-granted row exists), which is the ACTUAL reason "the Joyride
	 * does not work at all" — the picker step never reaches a stable frame for Joyride (or
	 * anything else) to measure, regardless of whether a tour is running:
	 *
	 *   1. This list learns row G is already-granted -> reports it -> `useScopeSelection`'s
	 *      `alreadyGranted` state changes -> its `exclude[]` memo changes (now includes G).
	 *   2. The NEW `exclude[]` reaches every `ScopePickerList` as a prop, including this one.
	 *      Per §3.4, ANY exclude[] change resets this list's OWN pagination to page 1 —
	 *      correct in general (rule 3.4 is intentional), but this reset SYNCHRONOUSLY clears
	 *      `items` back to `[]` before the resulting refetch has resolved.
	 *   3. The (then-)existing effect derived the already-granted report FROM `items`, so the
	 *      transient, mid-flight `items === []` was reported as "G is no longer already
	 *      granted" — which flips step 1's state back off, changes `exclude[]` back, and
	 *      resets this list again. The refetch then re-discovers G (self-exclusion, §3.3,
	 *      keeps a node in its own list), reports it again, and the cycle repeats without end.
	 *
	 * Fix: "already granted" is a fact about a (userId, roleId) SUBJECT, not about this list's
	 * current page/search state, so it must not be re-derived from `items` (which a
	 * query/exclude-driven reset legitimately, transiently empties). Accumulate it separately in
	 * a ref that only ever GROWS for a given subject and is reported from the SAME guarded
	 * effect that accumulates `items` (so it only ever fires once genuinely NEW server data has
	 * landed, never from the reset's synchronous, transient empty state) — and only reset when
	 * the subject itself changes (a real "start over", not a pagination/search side effect).
	 */
	const subjectKey = `${userId ?? ""}:${roleId ?? ""}`;
	const committedSubjectKeyRef = useRef(subjectKey);
	const alreadyGrantedMapRef = useRef<Map<string, ScopeOption>>(new Map());
	if (committedSubjectKeyRef.current !== subjectKey) {
		committedSubjectKeyRef.current = subjectKey;
		alreadyGrantedMapRef.current = new Map();
	}

	/**
	 * PRO-757 R2, defence in depth (NOT the fix — that is `AddAccessFlow`'s own role-change
	 * reset, which owns the whole selection and is what the review asked for). The accumulator
	 * above is cleared when the subject changes, but the last report sent UPWARD was never
	 * retracted: the effect below only reports when something was ADDED, so a new subject with
	 * no already-granted rows of this type left the previous subject's array in place upstream,
	 * still driving `exclude[]`. Retracting it here keeps this component's own upward report
	 * consistent with the state it just cleared.
	 *
	 * Three deliberate details:
	 *  - it must be an effect, not part of the render-phase reset above — calling the parent's
	 *    setter during this component's render is illegal;
	 *  - it is declared BEFORE the accumulating effect so that, in a commit where both run (a
	 *    subject change whose response is already cached, so `isLoading` is false immediately),
	 *    the retraction lands first and the fresh accumulation last, never the other way round;
	 *  - it deliberately does NOT fire on the first render. There is nothing to retract on
	 *    mount, and reporting an empty set unprompted is exactly the oscillation author item
	 *    4.2's regression test forbids.
	 */
	const reportedSubjectKeyRef = useRef(subjectKey);
	useEffect(() => {
		if (reportedSubjectKeyRef.current === subjectKey) {
			return;
		}
		reportedSubjectKeyRef.current = subjectKey;
		onAlreadyGrantedChange(type, []);
	}, [subjectKey, type, onAlreadyGrantedChange]);

	if (resetSignature !== committedSignature) {
		setCommittedSignature(resetSignature);
		setPage(1);
		setItems([]);
		loadedPagesRef.current = new Set();
	}

	const { scopeOptionsPage, isLoading, error, refetch } = useScopeOptions(
		{
			type,
			q: query || undefined,
			page,
			exclude,
			userId,
			roleId,
		},
		enabled
	);

	// Accumulate "Load more" pages, and (in the SAME guarded pass) any newly-confirmed
	// already-granted rows. Guarded against: (a) the pre-resolution placeholder page
	// (useScopeOptions' EMPTY_PAGE also reports currentPage:1 while isLoading — without this
	// guard it would satisfy the page-match check below and get marked "already loaded" before
	// the real response for page 1 ever arrives, permanently dropping it), (b) re-appending a
	// page already recorded (react-query can re-deliver the same cached response on re-render),
	// (c) a stale/race response for a page we've since moved past.
	useEffect(() => {
		if (isLoading) {
			return;
		}
		// PRO-757 A3 — a failed request resolves to `useScopeOptions`' EMPTY_PAGE placeholder,
		// which reports `currentPage: 1`. Without this guard the failure would be recorded in
		// `loadedPagesRef` as "page 1 is loaded", and guard (b) below would then silently DROP
		// the real page 1 when a retry succeeded.
		if (error) {
			return;
		}
		if (scopeOptionsPage.currentPage !== page) {
			return;
		}
		if (loadedPagesRef.current.has(page)) {
			return;
		}
		loadedPagesRef.current.add(page);
		setItems((prev) => [...prev, ...scopeOptionsPage.data]);

		const newlyGranted = scopeOptionsPage.data.filter((option) => option.already_granted);
		let changed = false;
		newlyGranted.forEach((option) => {
			const key = scopeKey(option.scope_type, option.scope_id);
			if (!alreadyGrantedMapRef.current.has(key)) {
				alreadyGrantedMapRef.current.set(key, option);
				changed = true;
			}
		});
		if (changed) {
			onAlreadyGrantedChange(type, Array.from(alreadyGrantedMapRef.current.values()));
		}
	}, [scopeOptionsPage, page, isLoading, error, type, onAlreadyGrantedChange]);

	const hasMore = page < scopeOptionsPage.lastPage;

	return (
		<div className="rounded-lg border border-gray-200" data-testid={`scope-results-${type}`}>
			<div className="bg-gray-50 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
				{typeLabel}
			</div>
			{/* PRO-757 A6 — the server caps its working set at 500 rows per type BEFORE applying
			    exclusion and paginating (FOLLOW-UPS #2), and `total`/`last_page` then describe the
			    truncated set as though it were the whole one — so paging to the end proves
			    nothing. `meta.truncated` was plumbed all the way to this component and read by
			    nobody; this is the prompt FOLLOW-UPS #2 claims as the mitigation. Not an error:
			    what is shown is correct, just not exhaustive. */}
			{scopeOptionsPage.truncated && !isLoading && !error && (
				<div
					role="note"
					className="border-b border-amber-200 bg-amber-50 px-3.5 py-2 text-xs text-amber-900"
					data-testid={`scope-truncated-${type}`}
				>
					Too many matches to list them all — narrow your search to see the rest.
				</div>
			)}
			{/* PRO-757 A3 — a request that FAILED is not an empty list. Destructuring `error`
			    away let a 500/403/422 fall through to "No matches.", telling the admin the
			    server had nothing when it never answered at all. Same shape as
			    `BulkUserPickerModal`'s own error + retry. */}
			{error && (
				<div
					role="alert"
					className="m-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
					data-testid={`scope-error-${type}`}
				>
					<p className="mb-2">This list could not be loaded.</p>
					<button
						type="button"
						onClick={() => void refetch()}
						data-testid={`scope-retry-${type}`}
						className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
					>
						Try again
					</button>
				</div>
			)}
			{items.length === 0 && !isLoading && !error && (
				<div className="px-3.5 py-2 text-sm text-gray-400" data-testid={`scope-no-matches-${type}`}>
					No matches.
				</div>
			)}
			{items.map((option) => {
				const key = scopeKey(option.scope_type, option.scope_id);
				const selected = isSelected(option);
				const style = scopeTypeStyle(type);
				const RowIcon = style.icon;
				return (
					<button
						type="button"
						key={key}
						disabled={option.already_granted}
						onClick={() => onToggle(option)}
						data-testid={`scope-option-${key}`}
						aria-pressed={selected}
						className={cn(
							"flex w-full items-center gap-2.5 border-l-[3px] px-3.5 py-2 text-left text-sm",
							option.already_granted && "cursor-not-allowed opacity-70",
							// Author item 4.4 — a selected row is tinted in its OWN section's colour
							// family (emerald/amber/blue), not a flat blue shared by every type.
							!option.already_granted && selected
								? cn(style.rowSelectedBg, "hover:brightness-95")
								: "border-l-transparent hover:bg-gray-50"
						)}
					>
						{/* Author item F7 — every row gets an icon matching its access level (ticket
						    line 44), the same SCOPE_TYPE_ICON convention `ManageRolesTable.tsx` and the
						    deleted `Step2ScopeSearch.tsx` used — D10's rewrite carried the colour
						    convention into ScopePickerList but silently dropped the icon. */}
						<RowIcon
							size={16}
							className={cn(
								"shrink-0",
								style.iconClassName,
								selected ? style.rowSelectedText : "text-gray-400"
							)}
							aria-hidden="true"
							data-testid={`scope-option-icon-${key}`}
						/>
						<span className={selected ? cn("font-bold", style.rowSelectedText) : "font-medium"}>
							{rowLabelOverride ?? option.label}
						</span>
						<span className="ml-auto flex items-center gap-2 text-[11.5px] text-gray-500">
							{/* PRO-757 follow-up — author: "can we also show the projects of the RFx
							    Invites they have been invited to". `parent` is the server-derived
							    cascade breadcrumb, which for an RFx IS its project, so the name was
							    already here — as a bare grey string that reads as an afterthought.
							    Labelling it is the whole change: on a bidder-only company the RFx
							    list is the ONLY list, and this is the sole thing on screen saying
							    which project each invite sits under.

							    It stays read-only on purpose. Offering the project as a selectable
							    scope would be a wider grant than the invite it came from —
							    `ScopeResolver::contains()` puts EVERY RFx under a project inside a
							    project-scoped grant, invited or not, and it would keep covering RFx
							    added to that project later. */}
							{option.parent && (
								<span data-testid={`scope-option-parent-${key}`}>
									{parentPrefix(option.parent.scope_type)}
									{option.parent.label}
								</span>
							)}
							{option.already_granted && (
								<span
									className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800"
									data-testid={`already-granted-badge-${key}`}
								>
									Already Granted
								</span>
							)}
							{/* Author item 4.4 — a plain "· selected" marker in the row's own colour,
							    matching specific-access.png, not a coloured pill. */}
							{!option.already_granted && selected && (
								<span className={style.rowSelectedText}>· selected</span>
							)}
						</span>
					</button>
				);
			})}
			{isLoading && (
				<div className="px-3.5 py-2 text-sm text-gray-400" data-testid={`scope-loading-${type}`}>
					Searching…
				</div>
			)}
			{!isLoading && !error && hasMore && (
				<button
					type="button"
					onClick={() => setPage((prev) => prev + 1)}
					data-testid={`scope-option-load-more-${type}`}
					className="w-full px-3.5 py-2 text-left text-sm font-medium text-blue-600 hover:bg-blue-50"
				>
					Load more…
				</button>
			)}
		</div>
	);
};
